import React from 'react';
import { Sprout, Flame, Package, LayoutGrid } from 'lucide-react';
import type { InventoryTypeFilter } from '../../../hooks/inventory-hooks/useInventoryPageState';
import { useLanguage } from '../../../features/language/useLanguage';
import { cn } from '../../../utils/cn';

interface InventoryTypeFilterProps {
  typeFilter: InventoryTypeFilter;
  onChange: (value: InventoryTypeFilter) => void;
}

const ORDER: InventoryTypeFilter[] = ['green_bean', 'roasted_bulk', 'finished_good', 'all'];

export const InventoryTypeSelector: React.FC<InventoryTypeFilterProps> = ({ typeFilter, onChange }) => {
  const { t } = useLanguage();

  const items: {
    value: InventoryTypeFilter;
    label: string;
    icon: React.ReactNode;
    testId: string;
  }[] = [
    {
      value: 'green_bean',
      label: t('inventory.typeFilter.greenBeans'),
      icon: <Sprout className="h-3.5 w-3.5" />,
      testId: 'inventory-type-switch-green-beans',
    },
    {
      value: 'roasted_bulk',
      label: t('inventory.typeFilter.roastedBulks'),
      icon: <Flame className="h-3.5 w-3.5" />,
      testId: 'inventory-type-switch-roasted-bulks',
    },
    {
      value: 'finished_good',
      label: t('inventory.typeFilter.finishedGoods'),
      icon: <Package className="h-3.5 w-3.5" />,
      testId: 'inventory-type-switch-finished-goods',
    },
    {
      value: 'all',
      label: t('forms.common.all'),
      icon: <LayoutGrid className="h-3.5 w-3.5" />,
      testId: 'inventory-type-switch-all',
    },
  ];

  const activeIndex = Math.max(0, ORDER.indexOf(typeFilter));

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {t('inventory.typeFilter.itemType')}
      </label>
      <div
        role="tablist"
        aria-label={t('inventory.typeFilter.itemType')}
        className="relative grid w-full grid-cols-[repeat(4,1fr)] rounded-lg bg-gray-100 p-1 ring-1 ring-inset ring-gray-200/80 sm:w-fit"
      >
        <span
          aria-hidden="true"
          className="absolute top-1 bottom-1 left-1 rounded-md bg-primary-500 shadow-sm ring-1 ring-inset ring-primary-600/20 transition-transform duration-200 ease-out"
          style={{
            width: 'calc((100% - 0.5rem) / 4)',
            transform: `translateX(${activeIndex * 100}%)`,
          }}
        />
        {items.map(({ value, label, icon, testId }) => {
          const active = typeFilter === value;
          return (
            <button
              key={value}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => onChange(value)}
              data-test-id={testId}
              title={label}
              className={cn(
                'relative z-10 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-md transition-colors whitespace-nowrap',
                active ? 'text-white' : 'text-gray-500 hover:text-gray-800'
              )}
            >
              <span className="shrink-0">{icon}</span>
              <span className="hidden sm:inline">{label}</span>
              <span className="sr-only sm:hidden">{label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};
